import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { CommentEditor } from '@atlaskit/editor-core/appearance-editor-comment';
import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { CommentEditorWithoutSpinner } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { handleAgentMentionInComment } from '@confluence/agent-mention/entry-points/handleAgentMentionInComment.ts';
import { useAgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/useAgentMentionFyiNudge.ts';
import { AgentMentionFyiNudgeAsync as AgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/AgentMentionFyiNudgeAsync.ts';
import { isOptimisticSaveEnabledForTypeAndMode } from '@confluence/comment/entry-points/draftUtils.ts';
import type { EditorCommentMode } from '@confluence/comment/entry-points/commentTypes.ts';
import {
	ADD_PAGE_COMMENT_EXPERIENCE,
	EDIT_PAGE_COMMENT_EXPERIENCE,
	REPLY_TO_PAGE_COMMENT_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { usePageContentId, useContentType } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';

import type {
	PageCommentEditorContentOperationsQuery as PageCommentEditorContentOperationsQuery$data,
	PageCommentEditorContentOperationsQueryVariables as PageCommentEditorContentOperationsQuery$variables,
} from './__types__/PageCommentEditorContentOperationsQuery.ts';
import type { CommentSaveHandler } from './commentSimpleTypes.ts';

type PageCommentEditorProps = {
	mode: EditorCommentMode;
	saveHandler: CommentSaveHandler;
	cancelHandler: ({ isEditorAborted }: { isEditorAborted: any }) => void;
	onEditorLoaded: () => void;
	onEditorReady?: (editorActions: any) => void;
	onEditorContentChanged?: (hasChanges: boolean) => void;
	onEditorContentChangedWithData?: (hasChanges: boolean, content?: DocNode) => void;
	afterActivate?: () => void;
	content?: DocNode;
	useNewExperienceAbort?: boolean;
	expandEditor?: boolean;
	isCommentsPanel?: boolean;
	isFooterComment?: boolean;
	isOffline?: boolean;
	showAgentMentionFyiNudge?: boolean;
};

export const PageCommentEditor: FC<PageCommentEditorProps> = ({
	mode,
	saveHandler,
	cancelHandler,
	onEditorLoaded,
	onEditorReady,
	onEditorContentChanged,
	onEditorContentChangedWithData,
	afterActivate,
	content,
	useNewExperienceAbort,
	expandEditor,
	isCommentsPanel,
	isFooterComment = false,
	isOffline = false,
	showAgentMentionFyiNudge = true,
}) => {
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const spaceId = useSpaceId();
	const { cloudId, userId } = useSessionData();
	const {
		showFyi,
		agent,
		onContentChange: onAgentMentionContentChange,
	} = useAgentMentionFyiNudge({
		cloudId,
		enabled:
			showAgentMentionFyiNudge &&
			expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true),
	});

	// Track whether the FYI component has ever been shown, so we only load the
	// chunk on first mention but keep it mounted for the collapse animation.
	const hasShownFyiRef = useRef(false);
	useEffect(() => {
		if (showFyi) {
			hasShownFyiRef.current = true;
		}
	}, [showFyi]);

	const handleContentChangeWithData = useCallback(
		(hasChanges: boolean, adf?: DocNode) => {
			onEditorContentChangedWithData?.(hasChanges, adf);
			onAgentMentionContentChange(adf);
		},
		[onEditorContentChangedWithData, onAgentMentionContentChange],
	);

	const { data } = useQueryInterop<
		PageCommentEditorContentOperationsQuery$data,
		PageCommentEditorContentOperationsQuery$variables
	>(
		graphql`
			query PageCommentEditorContentOperationsQuery($contentId: ID!, $status: [String]) {
				content(id: $contentId, status: $status) {
					nodes {
						id
						type
						space {
							id
							operations {
								operation
								targetType
							}
						}
						operations {
							operation
							targetType
						}
					}
				}
			}
		`,
		{
			variables: { contentId: contentId ?? '' },
			skip: !contentId,
		},
	);

	const experienceTracker: any = useContext(ExperienceTrackerContext);
	const operations = useMemo(() => data?.content?.nodes?.[0]?.operations ?? [], [data]);

	// User space permissions - only access when feature gate is on
	const spaceOperations = useMemo(
		() =>
			fg('confluence_comments_space_operations_permissions')
				? data?.content?.nodes?.[0]?.space?.operations || []
				: [],
		[data],
	);

	const canAddComment = useMemo(
		() => operations.some((op) => op?.operation === 'create' && op?.targetType === 'comment'),
		[operations],
	);

	const canAddAttachment = useMemo(
		() =>
			operations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment') ||
			spaceOperations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment'),
		[operations, spaceOperations],
	);

	const hasMediaUploadPermissions = canAddComment && canAddAttachment;

	// Simulate the unmount function for the other editor
	useEffect(
		() => () => {
			cancelHandler({ isEditorAborted: false });
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	useEffect(() => {
		const handleExperienceAbort = () => {
			const reason = 'Navigated away from page';
			if (mode === 'edit') {
				experienceTracker.abort({
					name: EDIT_PAGE_COMMENT_EXPERIENCE,
					reason,
				});
			} else if (mode === 'reply') {
				experienceTracker.abort({
					name: REPLY_TO_PAGE_COMMENT_EXPERIENCE,
					reason,
				});
			} else {
				experienceTracker.abort({
					name: ADD_PAGE_COMMENT_EXPERIENCE,
					reason,
				});
			}
		};

		if (useNewExperienceAbort) {
			window.addEventListener('beforeunload', handleExperienceAbort);
		}

		return () => {
			if (useNewExperienceAbort) {
				window.removeEventListener('beforeunload', handleExperienceAbort);
			}
		};
	}, [experienceTracker, mode, useNewExperienceAbort]);

	const handleSave = useCallback(
		(adf: any, onSuccess: () => void) =>
			saveHandler({ adf }, onSuccess)
				.then((result) => {
					if (expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)) {
						// `result` is { id: comment.id } returned by the parent saveHandler
						// (ObjectAddCommentEditor or CommentsPanelFooter) after the mutation resolves.
						const commentId = (result as { id?: string })?.id ?? '';
						handleAgentMentionInComment(
							adf,
							cloudId,
							contentId ?? '',
							commentId,
							userId ?? undefined,
							'footer',
						);
					}
					return result;
				})
				.catch((err) => {
					// There is another handler higher up to re-enable the buttons, throw the error to allow for that
					throw err;
				}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[saveHandler, cloudId, contentId, userId],
	);

	const handleCancel = useCallback(
		(params?: { destroy?: boolean }) => {
			// handleCancel() is invoked by CommentEditor on its destruction, with
			// `destroy` set to true. In this case, `isEditorAborted` should be
			// false since user didn't explicitly abort commenting. If user did
			// abort, `destroy` will be false, and we should pass `isEditorAborted`
			// as true into cancelHandler().
			const isEditorAborted = isOptimisticSaveEnabledForTypeAndMode('page', 'create')
				? !params?.destroy
				: true;
			cancelHandler({ isEditorAborted });
		},
		[cancelHandler],
	);

	const handleEditorReady = useCallback(
		(editorActions: any) => {
			onEditorLoaded();
			onEditorReady?.(editorActions);
		},
		[onEditorLoaded, onEditorReady],
	);

	const agentMentionFyiNudge =
		showAgentMentionFyiNudge &&
		(showFyi || hasShownFyiRef.current) &&
		expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) ? (
			<AgentMentionFyiNudge visible={showFyi} agent={agent} />
		) : null;

	return (
		<>
			{agentMentionFyiNudge}
			<CommentEditorWithoutSpinner
				pageId={contentId!}
				pageType={contentType!}
				appearance={isCommentsPanel ? 'chromeless' : 'comment'}
				EditorComponent={isCommentsPanel ? ChromelessEditor : CommentEditor}
				commentMode={mode}
				commentType="page"
				spaceId={spaceId!}
				onSaveComment={handleSave}
				content={content}
				onCancelComment={handleCancel}
				onEditorReady={handleEditorReady}
				onContentChange={onEditorContentChanged}
				onContentChangeWithData={handleContentChangeWithData}
				afterActivation={afterActivate}
				hasMediaUploadPermissions={hasMediaUploadPermissions}
				shouldWarnOnInternalNavigation
				expandEditor={expandEditor}
				showCancelButton={isCommentsPanel || isFooterComment}
				useNewWarningModal={isCommentsPanel || isFooterComment}
				isCommentsPanel={isCommentsPanel}
				isFooterComment={isFooterComment}
				isOffline={isOffline}
			/>
		</>
	);
};
